import {
  Box,
  Chip,
  Modal,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import React from "react";

import {
  Crane,
  generatedApi,
  LoadBasketRead,
  useGetLoadQuery,
  useUpdateLoadBasketAssignmentMutation,
} from "src/store/api/generatedApi";
import { useAppDispatch } from "src/store/store";

import { useTenantTranslation } from "hooks/formatters";
import { useTenant } from "hooks/settings";
import { useMixQuantity } from "hooks/useMixQuantity";

import { Basket } from "./Basket";
import { refreshMS } from "./utils";

interface BodyProps {
  baskets: LoadBasketRead[];
  cranes: Crane[];
  steelGrade: string;
  loadId: number;
}

interface WrapperProps {
  loadId: number;
  cranes: Crane[];
}

interface Props {
  onClose: () => void;
  loadId: number | null;
  cranes: Crane[];
}

const SkeletonBody = () => {
  return <Skeleton />;
};

const Body = ({ baskets, cranes, steelGrade, loadId }: BodyProps) => {
  const [isAssigning, setIsAssigning] = React.useState(false);

  const theme = useTheme();
  const { t } = useTenantTranslation();
  const tenantName = useTenant();
  const mixQuantity = useMixQuantity();
  const dispatch = useAppDispatch();

  const [updateLoadBasketAssignment] = useUpdateLoadBasketAssignmentMutation();

  const handleCraneChipClick = React.useCallback(
    async (loadBasketId: number, craneUID: string | null) => {
      setIsAssigning(true);
      await updateLoadBasketAssignment({
        tenantName,
        loadBasketId,
        loadBasketAssignmentUpdate: { assigned_to_uid: craneUID },
      });
      await dispatch(
        generatedApi.endpoints.getLoad.initiate(
          { tenantName, loadId },
          { subscribe: false, forceRefetch: true }
        )
      );
      setIsAssigning(false);
    },
    [updateLoadBasketAssignment, tenantName, loadId, dispatch, setIsAssigning]
  );

  return (
    <Stack gap={1}>
      <Typography variant="h4">{steelGrade}</Typography>
      <Box
        id="modal-load-basket-container"
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 5,
        }}
      >
        {baskets.map((basket) => {
          const isActive = basket.status === "partially_filled";
          const isComplete = basket.status === "filled";

          return (
            <Stack // Basket Cards in the Load Modal
              className="load-basket-card"
              data-basketNumber={basket.basket_number}
              data-steelGrade={steelGrade}
              data-loadId={loadId}
              key={basket.basket_number}
              sx={{
                borderWidth: 1,
                borderStyle: "solid",
                borderColor: theme.palette.divider,
                boxShadow: theme.shadows[1],
                borderRadius: 1,
                paddingTop: 1,
                paddingX: 2,
                paddingBottom: 4,
                rowGap: 2,
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="h5">
                  {t("basket")} {basket.basket_number}
                </Typography>
                {isActive ? <Chip label="active" color="success" /> : null}
              </Box>
              <Box
                sx={{
                  display: "flex",
                  gridColumn: 1,
                  gap: 0.5,
                  flexWrap: "wrap",
                }}
              >
                <Typography
                  sx={{ flexBasis: "100%", opacity: isComplete ? 0.5 : 1 }}
                  variant="subtitle2"
                >
                  {t("assignedTo")}
                </Typography>
                {cranes
                  .filter(
                    (crane) =>
                      basket.assigned_to_uid === crane.user_uid ||
                      basket.assigned_to_uid === null
                  )
                  .map((crane) => {
                    const isAssignedToBasket =
                      basket.status !== "filled" &&
                      basket.assigned_to_uid === crane.user_uid;

                    return (
                      <Chip // Crane chip/buttons in the Load Modal
                        className="load-basket-crane-chip"
                        data-loadBasketId={basket.load_basket_id}
                        data-craneUID={crane.user_uid}
                        data-isAssigned={isAssignedToBasket}
                        key={crane.user_uid}
                        label={crane.name}
                        disabled={isActive || isComplete || isAssigning}
                        color={isAssignedToBasket ? "primary" : "default"}
                        onClick={() => {
                          if (isAssignedToBasket) {
                            void handleCraneChipClick(
                              basket.load_basket_id,
                              null
                            );
                          } else {
                            void handleCraneChipClick(
                              basket.load_basket_id,
                              crane.user_uid
                            );
                          }
                        }}
                      />
                    );
                  })}
              </Box>
              <Basket
                key={basket.basket_number}
                sx={{ gridColumn: "1/-1" }}
                size="medium"
                layers={basket.materials}
                completed={isComplete}
                mixQuantity={mixQuantity}
              />
            </Stack>
          );
        })}
      </Box>
    </Stack>
  );
};

const Wrapper = ({ loadId, cranes }: WrapperProps) => {
  const tenantName = useTenant();
  const load = useGetLoadQuery(
    { tenantName, loadId },
    { pollingInterval: refreshMS }
  );
  if (load.data) {
    return (
      <Body
        baskets={Object.values(load.data.baskets)}
        cranes={cranes}
        loadId={loadId}
        steelGrade={load.data.steel_grade_name}
      />
    );
  }
  return <SkeletonBody />;
};

export const LoadModal = ({ onClose, loadId, cranes }: Props) => {
  return (
    <Modal open={loadId !== null} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          minHeight: 600,
          minWidth: 150,
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          backgroundColor: "white",
          padding: 2,
        }}
      >
        {loadId === null ? null : <Wrapper loadId={loadId} cranes={cranes} />}
      </Box>
    </Modal>
  );
};
